import { useEffect, useState } from "react";
import { Box, InputLabel, MenuItem, Select, Typography, useMediaQuery } from "@mui/material";
import { CustomInput } from "global/components";
import AppModal from "global/components/AppModal/AppModal";
import customInputStyles from "global/components/CustomInput/CustomInput.styles";
import formFieldMetrics from "global/components/CustomInput/FormField.tokens";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import { MAX_WORKSPACE_DEPTH, Workspace } from "types/workspace";
import { createWorkspace, moveWorkspace } from "../WorkspacesServices";
import { heightOf, pathOf, subtreeIds } from "../workspaceTree";
import { notifyWorkspacesChanged } from "../workspaceEvents";
import {
  fieldHelperSx,
  formSelectSx,
  modalFieldsSx,
  selectPlaceholderSx,
  workspaceMenuPaperSx,
} from "../Workspaces.styles";

const MOVE_SELECT_ID = "workspace-move-parent";
// The Select's value for "no parent": an empty string would read as "nothing chosen yet".
const TOP_LEVEL = "__top__";
// Below 370px the phone sheet's half-width button can't fit "Create sub-workspace"; the title
// already says "New sub-workspace", so the button says just "Create" there.
const NARROW_SCREEN_QUERY = "(max-width:369.98px)";

/** "Sales › EMEA" — where a workspace sits, for the move picker. */
const pathLabel = (all: Workspace[], id: string) =>
  pathOf(all, id)
    .map((w) => w.name)
    .join(" › ");

interface AddProps {
  /** The workspace the new one goes under; null closes the dialog. */
  parent: Workspace | null;
  onClose: () => void;
  onCreated: () => void;
}

/** Creates a sub-workspace under `parent`. */
export const AddSubWorkspaceDialog = ({ parent, onClose, onCreated }: AddProps) => {
  const [name, setName] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const narrowScreen = useMediaQuery(NARROW_SCREEN_QUERY);

  useEffect(() => {
    if (parent) setName("");
  }, [parent]);

  const submit = async () => {
    const trimmed = name.trim();
    if (!parent || !trimmed || submitting) return;
    try {
      setSubmitting(true);
      await createWorkspace(trimmed, parent.id);
      openSuccessNotification(`"${trimmed}" created under "${parent.name}".`);
      notifyWorkspacesChanged();
      onClose();
      onCreated();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppModal
      open={Boolean(parent)}
      onClose={onClose}
      title="New sub-workspace"
      subtitle={parent ? `It will sit under "${parent.name}". You can rename it later.` : undefined}
      size="sm"
      busy={submitting}
      primaryLabel={narrowScreen ? "Create" : "Create sub-workspace"}
      onPrimary={submit}
      primaryLoading={submitting}
      primaryDisabled={!name.trim()}
      cancelLabel="Cancel"
    >
      <CustomInput
        autoFocus
        label="Workspace name"
        placeHolder="Enter workspace name"
        type="text"
        name="subWorkspaceName"
        value={name}
        onChange={(e: any) => setName(e.target.value)}
        onKeyPress={(e: any) => {
          if (e.key === "Enter") submit();
        }}
      />
    </AppModal>
  );
};

interface MoveProps {
  /** The workspace to move; null closes the dialog. */
  workspace: Workspace | null;
  /** Every workspace in the account (the move picker lists the valid new parents from it). */
  all: Workspace[];
  onClose: () => void;
  onMoved: () => void;
}

/**
 * Gives a workspace a new parent, or moves it to the top level. Only lists parents the backend
 * would accept: not the workspace itself or anything below it, not archived, and deep enough
 * room for the workspace and its own sub-workspaces.
 */
export const MoveWorkspaceDialog = ({ workspace, all, onClose, onMoved }: MoveProps) => {
  const [target, setTarget] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (workspace) setTarget("");
  }, [workspace]);

  if (!workspace) {
    return <AppModal open={false} onClose={onClose} />;
  }

  const below = subtreeIds(all, workspace.id);
  const height = heightOf(all, workspace.id);
  const options = all.filter(
    (w) =>
      !below.has(w.id) &&
      !w.archived &&
      (w.depth ?? pathOf(all, w.id).length) + 1 + height <= MAX_WORKSPACE_DEPTH,
  );
  const currentParent = workspace.parentId ?? null;
  const chosenParent = target === TOP_LEVEL ? null : target;
  const unchanged = target === "" || chosenParent === currentParent;

  const submit = async () => {
    if (unchanged || submitting) return;
    try {
      setSubmitting(true);
      await moveWorkspace(workspace.id, chosenParent);
      const where = chosenParent
        ? `under "${all.find((w) => w.id === chosenParent)?.name ?? "the new parent"}"`
        : "to the top level";
      openSuccessNotification(`"${workspace.name}" moved ${where}.`);
      notifyWorkspacesChanged();
      onClose();
      onMoved();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSubmitting(false);
    }
  };

  const label = (value: string) =>
    value === TOP_LEVEL ? "Top level (no parent)" : pathLabel(all, value);

  return (
    <AppModal
      open
      onClose={onClose}
      title={`Move "${workspace.name}"`}
      subtitle="Choose the workspace it should sit under. Its sub-workspaces move with it."
      size="md"
      busy={submitting}
      primaryLabel="Move workspace"
      onPrimary={submit}
      primaryLoading={submitting}
      primaryDisabled={unchanged}
      cancelLabel="Cancel"
    >
      <Box sx={modalFieldsSx}>
        <Box>
          <Box mb={formFieldMetrics.labelGap}>
            <InputLabel id={`${MOVE_SELECT_ID}-label`} sx={customInputStyles.inputLabel}>
              New parent
            </InputLabel>
          </Box>
          <Select
            labelId={`${MOVE_SELECT_ID}-label`}
            id={MOVE_SELECT_ID}
            displayEmpty
            value={target}
            onChange={(e) => setTarget(String(e.target.value))}
            renderValue={() =>
              target === "" ? (
                <Box component="span" sx={selectPlaceholderSx}>
                  Choose where to move it
                </Box>
              ) : (
                label(target)
              )
            }
            sx={formSelectSx}
            MenuProps={{ PaperProps: { sx: workspaceMenuPaperSx } }}
            fullWidth
          >
            <MenuItem value={TOP_LEVEL} disabled={currentParent === null}>
              {label(TOP_LEVEL)}
              {currentParent === null ? " (current)" : ""}
            </MenuItem>
            {options.map((w) => (
              <MenuItem key={w.id} value={w.id} disabled={w.id === currentParent}>
                {pathLabel(all, w.id)}
                {w.id === currentParent ? " (current)" : ""}
              </MenuItem>
            ))}
          </Select>
          <Typography sx={fieldHelperSx}>
            Only places with room for it are listed (up to {MAX_WORKSPACE_DEPTH} levels deep).
            Its envelope quota stays the same.
          </Typography>
        </Box>
      </Box>
    </AppModal>
  );
};
